import { useMemo } from 'react';
import { cn } from '@/lib/utils';

interface UserAvatarProps {
  id?: string | null;
  name?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  ringClass?: string;
  title?: string;
}

const SIZE_MAP = {
  xs: { box: 'w-5 h-5', text: 'text-[9px]' },
  sm: { box: 'w-7 h-7', text: 'text-[11px]' },
  md: { box: 'w-9 h-9', text: 'text-xs' },
  lg: { box: 'w-12 h-12', text: 'text-sm' },
};

const PALETTE = [
  'bg-rose-500',
  'bg-orange-500',
  'bg-amber-500',
  'bg-emerald-500',
  'bg-teal-500',
  'bg-sky-500',
  'bg-indigo-500',
  'bg-violet-500',
  'bg-fuchsia-500',
  'bg-pink-500',
];

function hashId(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

function initials(name?: string | null, email?: string | null): string {
  const src = (name || email || '?').trim();
  if (!src) return '?';
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export default function UserAvatar({
  id, name, email, avatarUrl, size = 'sm', className, ringClass, title,
}: UserAvatarProps) {
  const dims = SIZE_MAP[size];
  const colorClass = useMemo(() => {
    const seed = id || name || email || 'x';
    return PALETTE[hashId(seed) % PALETTE.length];
  }, [id, name, email]);

  const tip = title ?? (name || email || undefined);

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name || email || 'avatar'}
        title={tip}
        className={cn(
          dims.box,
          'rounded-full object-cover flex-shrink-0',
          ringClass,
          className,
        )}
      />
    );
  }

  return (
    <div
      title={tip}
      className={cn(
        dims.box,
        'rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 select-none',
        dims.text,
        colorClass,
        ringClass,
        className,
      )}
    >
      {initials(name, email)}
    </div>
  );
}
